import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FALLBACK_LANGUAGE } from "../config";
import { getAuthHeaders } from "../api/auth";
import { WorkflowRenderer } from "../workflow/WorkflowRenderer";
import type {
  WorkflowUIConfig,
  WorkflowUITile,
  WorkflowUITileAction,
  WorkflowUIStep
} from "../workflow/types";
import type { PathContext } from "../workflow/utils";
import {
  resolveBindingValue,
  resolveTemplateValue,
  setPath,
  translateText
} from "../workflow/utils";
import type { WorkflowDocument } from "./types";

interface WorkflowStandaloneViewProps {
  workflowId: string;
  workflow: WorkflowDocument;
  apiBaseUrl: string;
  language: string;
  onUnauthorized?: (message?: string) => void;
}

type SubmissionStatus = { kind: "success" | "error" | "info"; message: string } | null;

type WorkflowExecutionResponse = {
  status: "ok" | string;
  workflowId: string;
  context?: Record<string, unknown> | null;
  createdBusinessObject?: Record<string, unknown> | null;
};

type LocalizedMessageMap = Record<string, string>;

type DefaultErrorMessageMap = typeof DEFAULT_ERROR_MESSAGES[keyof typeof DEFAULT_ERROR_MESSAGES];

const DEFAULT_ERROR_MESSAGES = {
  "de-DE": {
    required: "Bitte dieses Feld ausfüllen.",
    selection: "Bitte eine Auswahl treffen.",
    invalid: "Bitte eine gültige Eingabe tätigen."
  },
  "en-US": {
    required: "Please fill out this field.",
    selection: "Please choose an option.",
    invalid: "Please provide a valid value."
  }
} as const;

const WORKFLOW_SUCCESS_MESSAGES: Record<string, string> = {
  "de-DE": "Workflow erfolgreich abgeschlossen.",
  "de": "Workflow erfolgreich abgeschlossen.",
  "en-US": "Workflow completed successfully.",
  "en": "Workflow completed successfully."
};

const WORKFLOW_TOOL_USAGE_MESSAGES: Record<string, string> = {
  "de-DE": "Genutzte Datenquellen:",
  "de": "Genutzte Datenquellen:",
  "en-US": "Data sources used:",
  "en": "Data sources used:"
};

const WORKFLOW_ERROR_MESSAGES = {
  missingId: {
    "de-DE": "Der Workflow konnte nicht ermittelt werden.",
    "de": "Der Workflow konnte nicht ermittelt werden.",
    "en-US": "Couldn't determine the workflow id.",
    "en": "Couldn't determine the workflow id."
  } satisfies LocalizedMessageMap,
  submitFailed: {
    "de-DE": "Workflow konnte nicht ausgeführt werden.",
    "de": "Workflow konnte nicht ausgeführt werden.",
    "en-US": "Couldn't execute the workflow.",
    "en": "Couldn't execute the workflow."
  } satisfies LocalizedMessageMap,
  unknownStep: {
    "de-DE": "Der nächste Schritt konnte nicht gefunden werden.",
    "de": "Der nächste Schritt konnte nicht gefunden werden.",
    "en-US": "The requested step could not be found.",
    "en": "The requested step could not be found."
  } satisfies LocalizedMessageMap
};

const WORKFLOW_RESET_MESSAGES = {
  "de-DE": "Neuer Check-in in {seconds} Sekunden …",
  "de": "Neuer Check-in in {seconds} Sekunden …",
  "en-US": "Next check-in in {seconds} seconds …",
  "en": "Next check-in in {seconds} seconds …"
} satisfies LocalizedMessageMap;

const RESET_DURATION_SECONDS = 20;

export function WorkflowStandaloneView(props: WorkflowStandaloneViewProps) {
  const { workflow, workflowId, apiBaseUrl, language: preferredLanguage, onUnauthorized } = props;

  const workflowInfo = useMemo<Record<string, unknown>>(() => cloneRecord(workflow), [workflow]);
  const uiConfig = useMemo<WorkflowUIConfig | null>(() => {
    const rawUi = workflowInfo.ui;
    return isWorkflowUIConfig(rawUi) ? rawUi : null;
  }, [workflowInfo]);

  const [workflowState, setWorkflowState] = useState<Record<string, unknown>>(() => ({ ...workflowInfo }));
  useEffect(() => {
    setWorkflowState({ ...workflowInfo });
  }, [workflowInfo]);

  const initialSessionData = useMemo(() => deriveInitialSession(workflowInfo.session, preferredLanguage), [workflowInfo.session, preferredLanguage]);

  const [session, setSession] = useState<Record<string, unknown>>(() => initialSessionData.session);
  const [activeLanguage, setActiveLanguage] = useState<string>(initialSessionData.language);
  useEffect(() => {
    setSession(initialSessionData.session);
    setActiveLanguage(initialSessionData.language);
  }, [initialSessionData]);

  const [currentStepId, setCurrentStepId] = useState<string | null>(() => uiConfig?.steps?.[0]?.id ?? null);
  useEffect(() => {
    const firstStep = uiConfig?.steps?.[0]?.id ?? null;
    setCurrentStepId(firstStep);
    setValidationErrors({});
  }, [uiConfig]);

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState<SubmissionStatus>(null);
  const [extras, setExtras] = useState<Record<string, unknown> | null>(null);
  const [createdBusinessObject, setCreatedBusinessObject] = useState<Record<string, unknown> | null>(null);
  const [resetCountdown, setResetCountdown] = useState<number | null>(null);
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearResetTimer = useCallback(() => {
    if (resetTimerRef.current) {
      clearTimeout(resetTimerRef.current);
      resetTimerRef.current = null;
    }
  }, []);

  const performFullReset = useCallback(() => {
    clearResetTimer();
    setResetCountdown(null);

    const refreshedWorkflow = cloneRecord(workflowInfo);
    setWorkflowState(refreshedWorkflow);

    const rawSession = (refreshedWorkflow as { session?: unknown }).session;
    const nextSessionData = deriveInitialSession(rawSession, preferredLanguage);
    setSession(nextSessionData.session);
    setActiveLanguage(nextSessionData.language);

    setExtras(null);
    setCreatedBusinessObject(null);
    setValidationErrors({});
    setStatusMessage(null);
    setSubmitting(false);

    const firstStep = uiConfig?.steps?.[0]?.id ?? null;
    setCurrentStepId(firstStep);
  }, [clearResetTimer, preferredLanguage, uiConfig, workflowInfo]);

  const startResetCountdown = useCallback(() => {
    if (resetCountdown !== null) {
      return;
    }
    clearResetTimer();
    setResetCountdown(RESET_DURATION_SECONDS);
  }, [clearResetTimer, resetCountdown]);

  useEffect(() => {
    if (resetCountdown === null) {
      return;
    }

    if (resetCountdown <= 0) {
      performFullReset();
      return;
    }

    resetTimerRef.current = setTimeout(() => {
      setResetCountdown((prev) => (prev != null ? prev - 1 : null));
    }, 1000);

    return () => {
      clearResetTimer();
    };
  }, [clearResetTimer, performFullReset, resetCountdown]);

  useEffect(() => () => {
    clearResetTimer();
  }, [clearResetTimer]);

  const resetProgress = useMemo(() => {
    if (resetCountdown === null) {
      return 0;
    }
    const elapsed = RESET_DURATION_SECONDS - resetCountdown;
    return Math.min(Math.max(elapsed / RESET_DURATION_SECONDS, 0), 1);
  }, [resetCountdown]);

  const totalSteps = uiConfig?.steps?.length ?? 0;
  const currentStep = useMemo<WorkflowUIStep | null>(() => {
    if (!uiConfig || !currentStepId) {
      return null;
    }
    return uiConfig.steps.find((step) => step.id === currentStepId) ?? null;
  }, [uiConfig, currentStepId]);

  const currentStepIndex = useMemo<number>(() => {
    if (!uiConfig || !currentStepId) {
      return -1;
    }
    return uiConfig.steps.findIndex((step) => step.id === currentStepId);
  }, [uiConfig, currentStepId]);

  const aiSummary = useMemo<string | null>(() => extractAiSummary(extras), [extras]);
  const toolsUsed = useMemo<string[]>(() => extractToolsUsed(extras), [extras]);
  const extendedExtras = useMemo<Record<string, unknown> | null>(() => {
    const base: Record<string, unknown> = extras ? cloneRecord(extras) : {};
    let hasAny = extras ? Object.keys(base).length > 0 : false;

    if (aiSummary) {
      base.aiSummary = aiSummary;
      hasAny = true;
    }

    if (toolsUsed.length > 0) {
      base.aiToolsUsed = toolsUsed;
      hasAny = true;
    }

    return hasAny ? base : null;
  }, [extras, aiSummary, toolsUsed]);

  const hasExtras = extendedExtras ? Object.keys(extendedExtras).length > 0 : false;
  const shouldShowResultPanel = Boolean(createdBusinessObject || aiSummary || toolsUsed.length > 0 || hasExtras);

  const workflowContext = useMemo<PathContext>(() => ({
    session,
    workflow: workflowState,
    uiAction: null,
    extras: extendedExtras
  }), [extendedExtras, session, workflowState]);

  const handleBindingChange = useCallback(
    (tile: WorkflowUITile, binding: string | undefined, value: unknown) => {
      if (!binding || !binding.startsWith("@session.")) {
        return;
      }

      const path = binding.slice("@session.".length);
      const nextSession = cloneRecord(session);
      setPath(nextSession, path, value);
      setSession(nextSession);

      setValidationErrors((prev) => {
        if (!prev[tile.id]) {
          return prev;
        }
        const updated = { ...prev };
        delete updated[tile.id];
        return updated;
      });

      if (bindingTargetsLanguage(binding)) {
        const candidate = typeof value === "string" && value.trim().length > 0
          ? value.trim()
          : extractLanguageFromSession(nextSession) ?? activeLanguage;
        setActiveLanguage(candidate);
      }
    },
    [activeLanguage, session]
  );

  const executeWorkflow = useCallback(
    async (action: WorkflowUITileAction, context: PathContext): Promise<WorkflowExecutionResponse> => {
      const targetWorkflowId = resolveWorkflowIdentifier(action, context, workflowId);
      if (!targetWorkflowId) {
        const message = pickLocalizedMessage(WORKFLOW_ERROR_MESSAGES.missingId, activeLanguage);
        throw new Error(message);
      }

      const resolvedInput = action.target?.payloadTemplate
        ? resolveTemplateValue(action.target.payloadTemplate, context)
        : undefined;

      const payload: Record<string, unknown> = {
        session: context.session,
        language: activeLanguage
      };

      if (resolvedInput !== undefined) {
        payload.input = resolvedInput as unknown;
      }

      const response = await fetch(
        `${apiBaseUrl}/api/workflows/${encodeURIComponent(targetWorkflowId)}/execute`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
            "Accept-Language": activeLanguage
          },
          body: JSON.stringify(payload)
        }
      );

      if (response.status === 401) {
        const errorMessage = await extractErrorMessage(response, activeLanguage);
        onUnauthorized?.(errorMessage);
        throw new Error(errorMessage);
      }

      if (!response.ok) {
        const errorMessage = await extractErrorMessage(response, activeLanguage);
        throw new Error(errorMessage);
      }

      const result = (await response.json()) as WorkflowExecutionResponse;
      if (result.status !== "ok") {
        const fallback = pickLocalizedMessage(WORKFLOW_ERROR_MESSAGES.submitFailed, activeLanguage);
        throw new Error(fallback);
      }

      return result;
    },
    [activeLanguage, apiBaseUrl, onUnauthorized, workflowId]
  );

  const handleAction = useCallback(
    async (action: WorkflowUITileAction) => {
      if (!uiConfig || !currentStepId) {
        return;
      }

      setStatusMessage(null);

      const context: PathContext = {
        session,
        workflow: workflowState,
        uiAction: null,
        extras: extendedExtras
      };

      const step = uiConfig.steps.find((candidate) => candidate.id === currentStepId) ?? null;
      const language = activeLanguage;
      const actionMode = action.mode ?? "default";
      const isIntermediateSubmit = actionMode === "intermediate";

      if (action.type === "finish") {
        const message = pickLocalizedMessage(WORKFLOW_SUCCESS_MESSAGES, language);
        setStatusMessage({ kind: "success", message });
        startResetCountdown();
        return;
      }

      const isNavigation = action.type === "navigate";
      const isSubmission = action.type === "submit";
      const targetStepId = isNavigation ? action.targetStepId : action.successStepId;
      const targetIndex = targetStepId
        ? uiConfig.steps.findIndex((candidate) => candidate.id === targetStepId)
        : -1;
      const isForwardNavigation = isSubmission || (isNavigation && targetIndex !== -1 && targetIndex > currentStepIndex);

      if (isForwardNavigation && step) {
        const errors = validateStep(step, context, language);
        if (Object.keys(errors).length > 0) {
          setValidationErrors(errors);
          return;
        }
        if (Object.keys(validationErrors).length > 0) {
          setValidationErrors({});
        }
      }

      if (action.type === "navigate") {
        if (action.targetStepId) {
          const targetExists = uiConfig.steps.some((candidate) => candidate.id === action.targetStepId);
          if (!targetExists) {
            const message = pickLocalizedMessage(WORKFLOW_ERROR_MESSAGES.unknownStep, language);
            setStatusMessage({ kind: "error", message });
            return;
          }
          setCurrentStepId(action.targetStepId);
        }
        return;
      }

      if (action.type === "message") {
        const message = deriveActionMessage(action, language);
        if (message) {
          setStatusMessage({ kind: "info", message });
        }
        return;
      }

      if (action.type !== "submit") {
        return;
      }

      setSubmitting(true);
      try {
        const result = await executeWorkflow(action, context);
        const contextPayload = isRecord(result.context) ? result.context : null;
        const sessionFromContext = contextPayload && isRecord(contextPayload.session)
          ? contextPayload.session
          : context.session;

        const derivedSession = deriveInitialSession(sessionFromContext, language);
        const hydratedSession = cloneRecord(derivedSession.session);

        if (isIntermediateSubmit) {
          const mergedSession = mergeBatchTransportIntoSession(hydratedSession, contextPayload);
          setSession(mergedSession);
          setActiveLanguage(derivedSession.language);

          if (contextPayload && isRecord(contextPayload.workflow)) {
            setWorkflowState(cloneRecord(contextPayload.workflow));
          }

          setExtras(null);
          setCreatedBusinessObject(null);
          setValidationErrors({});

          if (action.successStepId) {
            setCurrentStepId(action.successStepId);
          }

          setStatusMessage(null);
          return;
        }

        setSession(hydratedSession);
        setActiveLanguage(derivedSession.language);

        if (contextPayload && isRecord(contextPayload.workflow)) {
          setWorkflowState(cloneRecord(contextPayload.workflow));
        }

        setExtras(contextPayload);
        setCreatedBusinessObject(result.createdBusinessObject ? cloneRecord(result.createdBusinessObject) : null);
        setValidationErrors({});

        if (action.successStepId) {
          setCurrentStepId(action.successStepId);
        }

        const message = pickLocalizedMessage(WORKFLOW_SUCCESS_MESSAGES, derivedSession.language);
        setStatusMessage({ kind: "success", message });
        startResetCountdown();
      } catch (error) {
        const fallback = pickLocalizedMessage(WORKFLOW_ERROR_MESSAGES.submitFailed, activeLanguage);
        const message = error instanceof Error ? error.message : fallback;
        setStatusMessage({ kind: "error", message });
      } finally {
        setSubmitting(false);
      }
    },
    [activeLanguage, currentStepId, currentStepIndex, executeWorkflow, extendedExtras, session, startResetCountdown, uiConfig, validationErrors, workflowState]
  );

  if (!uiConfig || !Array.isArray(uiConfig.steps) || uiConfig.steps.length === 0) {
    return (
      <div className="standalone-workflow-placeholder">
        <h1>Workflow nicht verfügbar</h1>
        <p>Für diesen Workflow ist keine UI-Konfiguration vorhanden.</p>
      </div>
    );
  }

  if (!currentStepId) {
    return (
      <div className="standalone-workflow-placeholder">
        <h1>Workflow unvollständig</h1>
        <p>Der Workflow enthält keinen gültigen Startschritt.</p>
      </div>
    );
  }

  const workflowWrapperClassName = `standalone-workflow${uiConfig.fullscreen ? " standalone-workflow-fullscreen" : ""}`;

  return (
    <div className="standalone-container">
      <div className={workflowWrapperClassName}>
        <div className={`workflow-panel${submitting ? " workflow-panel-loading" : ""}`}>
          <header className="workflow-panel-header">
            <div className="workflow-panel-info">
              <strong>{typeof workflowState.name === "string" ? workflowState.name : "Workflow"}</strong>
              {totalSteps > 0 && currentStepIndex >= 0 ? (
                <span className="workflow-meta">
                  Schritt {currentStepIndex + 1} / {totalSteps}
                </span>
              ) : null}
              {submitting ? <span className="workflow-status">Verarbeite …</span> : null}
            </div>
          </header>
          {!submitting && resetCountdown !== null ? (
            <div className="workflow-reset-progress" role="status" aria-live="polite">
              <div className="workflow-reset-progress-bar" aria-hidden="true">
                <div
                  className="workflow-reset-progress-bar-fill"
                  style={{ width: `${Math.round(resetProgress * 100)}%` }}
                />
              </div>
              <span className="workflow-reset-progress-message">
                {formatResetMessage(activeLanguage, resetCountdown)}
              </span>
            </div>
          ) : null}
          {typeof workflowState.description === "string" && workflowState.description.trim().length > 0 ? (
            <p className="standalone-workflow-description">{workflowState.description}</p>
          ) : null}
          {statusMessage ? (
            <div className={`standalone-workflow-alert alert-${statusMessage.kind}`} role="status">
              {statusMessage.message}
            </div>
          ) : null}
          <div className="workflow-content">
            {currentStep ? (
              <WorkflowRenderer
                ui={uiConfig}
                stepId={currentStep.id}
                language={activeLanguage}
                fallbackLanguage={FALLBACK_LANGUAGE}
                context={workflowContext}
                validationErrors={validationErrors}
                onBindingChange={handleBindingChange}
                onAction={handleAction}
                disabled={submitting || resetCountdown !== null}
              />
            ) : (
              <div className="workflow-empty">
                <p>Dieser Schritt konnte nicht geladen werden.</p>
              </div>
            )}
          </div>
          {shouldShowResultPanel ? (
            <div className="standalone-workflow-result" aria-live="polite">
              <p className="standalone-workflow-result-title">
                {pickLocalizedMessage(WORKFLOW_SUCCESS_MESSAGES, activeLanguage)}
              </p>
              {aiSummary ? (
                <p className="standalone-workflow-result-text">{aiSummary}</p>
              ) : null}
              {toolsUsed.length > 0 ? (
                <p className="standalone-workflow-result-tools">
                  <span className="standalone-workflow-result-tools-label">
                    {pickLocalizedMessage(WORKFLOW_TOOL_USAGE_MESSAGES, activeLanguage)}
                  </span>
                  <span>{toolsUsed.join(", ")}</span>
                </p>
              ) : null}
            </div>
          ) : null}
          {submitting ? (
            <div className="workflow-loading-overlay" role="status" aria-live="polite">
              <div className="workflow-loading-spinner" />
              <p>Bitte einen Moment gedulden …</p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function mergeBatchTransportIntoSession(
  baseSession: Record<string, unknown>,
  contextPayload: Record<string, unknown> | null
): Record<string, unknown> {
  if (!contextPayload) {
    return baseSession;
  }

  const batchTransport = contextPayload.batchTransport;
  if (!isRecord(batchTransport)) {
    return baseSession;
  }

  const next = cloneRecord(baseSession);
  const truck = batchTransport.truckLicensePlate;

  if (typeof truck === "string" && truck.trim().length > 0) {
    setPath(next, "truckLicensePlate", truck.trim());
  }

  return next;
}

function deriveActionMessage(action: WorkflowUITileAction, language: string): string | null {
  if (typeof action.message === "string" && action.message.trim().length > 0) {
    return action.message.trim();
  }

  if (isRecord(action.message)) {
    const map = action.message as Record<string, string>;
    return map[language] ?? map[FALLBACK_LANGUAGE] ?? null;
  }

  return null;
}

function validateStep(
  step: WorkflowUIStep,
  context: PathContext,
  language: string
): Record<string, string> {
  const errors: Record<string, string> = {};
  const defaults: DefaultErrorMessageMap =
    DEFAULT_ERROR_MESSAGES[language as keyof typeof DEFAULT_ERROR_MESSAGES] ??
    DEFAULT_ERROR_MESSAGES[FALLBACK_LANGUAGE];

  for (const tile of step.tiles) {
    if (tile.componentType === "TextField") {
      const rawValue = resolveBindingValue(tile.bindings?.value, context);
      const value = typeof rawValue === "string" ? rawValue : rawValue != null ? String(rawValue) : "";
      const trimmed = value.trim();
      const pattern = tile.validation?.pattern ? new RegExp(tile.validation.pattern) : null;
      const optionalField = tile.validation?.optional === true;

      if (optionalField && trimmed.length === 0) {
        continue;
      }

      const isValid = pattern ? pattern.test(trimmed) : trimmed.length > 0;

      if (!isValid) {
        const errorMessage =
          translateText(tile.validation?.errorMessage, language, FALLBACK_LANGUAGE) ||
          (pattern ? defaults.invalid : defaults.required);
        errors[tile.id] = errorMessage;
      }
    }

    if (tile.componentType === "ChoiceGrid") {
      const selected = resolveBindingValue(tile.bindings?.value, context);
      if (!selected) {
        errors[tile.id] = defaults.selection;
      }
    }
  }

  return errors;
}

function resolveWorkflowIdentifier(
  action: WorkflowUITileAction,
  context: PathContext,
  fallbackWorkflowId: string
): string | undefined {
  if (!action.target || !action.target.workflowId) {
    const resolved = resolveBindingValue("@workflow._id", context);
    if (typeof resolved === "string" && resolved.trim().length > 0) {
      return resolved.trim();
    }
    return fallbackWorkflowId;
  }

  const workflowRef = action.target.workflowId;
  if (typeof workflowRef === "string" && workflowRef.length > 0) {
    if (workflowRef.startsWith("@")) {
      const resolved = resolveTemplateValue(workflowRef, context);
      if (typeof resolved === "string" && resolved.trim().length > 0) {
        return resolved.trim();
      }
      return fallbackWorkflowId;
    }
    return workflowRef;
  }

  return fallbackWorkflowId;
}

function bindingTargetsLanguage(binding: string): boolean {
  const normalized = binding.startsWith("@session.") ? binding.slice("@session.".length) : binding;
  return normalized.split(".").some((segment) => isLanguageKey(segment));
}

function deriveInitialSession(
  rawSession: unknown,
  preferredLanguage: string
): { session: Record<string, unknown>; language: string } {
  const session = cloneRecord(rawSession);
  const language = determineActiveLanguage(preferredLanguage, session);
  hydrateLanguagePlaceholders(session, language);
  return { session, language };
}

function determineActiveLanguage(
  preferredLanguage: string | undefined,
  session: Record<string, unknown>
): string {
  const candidates: Array<string | null | undefined> = [];

  candidates.push(extractLanguageFromSession(session));

  if (typeof preferredLanguage === "string" && preferredLanguage.trim().length > 0) {
    candidates.push(preferredLanguage.trim());
  }

  candidates.push(FALLBACK_LANGUAGE);

  const resolved = candidates.find((candidate) => typeof candidate === "string" && candidate.trim().length > 0);
  return resolved ?? FALLBACK_LANGUAGE;
}

function hydrateLanguagePlaceholders(target: Record<string, unknown>, language: string): void {
  const visit = (source: Record<string, unknown>) => {
    for (const [key, value] of Object.entries(source)) {
      if (isLanguageKey(key)) {
        if (typeof value === "string" && value.trim().length > 0) {
          source[key] = value.trim();
        } else {
          source[key] = language;
        }
        continue;
      }

      if (isRecord(value)) {
        visit(value);
      }
    }
  };

  visit(target);
}

function extractLanguageFromSession(session: Record<string, unknown>): string | null {
  for (const [key, value] of Object.entries(session)) {
    if (typeof value === "string" && isLanguageKey(key)) {
      const trimmed = value.trim();
      if (trimmed.length > 0) {
        return trimmed;
      }
    }

    if (isRecord(value)) {
      const nested = extractLanguageFromSession(value);
      if (nested) {
        return nested;
      }
    }
  }

  return null;
}

function isWorkflowUIConfig(value: unknown): value is WorkflowUIConfig {
  if (!isRecord(value)) {
    return false;
  }

  const candidate = value as { steps?: unknown };
  return Array.isArray(candidate.steps);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function cloneRecord(source: unknown): Record<string, unknown> {
  if (!isRecord(source)) {
    return {};
  }

  try {
    return JSON.parse(JSON.stringify(source)) as Record<string, unknown>;
  } catch {
    return { ...source } as Record<string, unknown>;
  }
}

function isLanguageKey(key: string): boolean {
  const normalized = key.toLowerCase();
  return normalized.includes("language") || normalized.includes("locale") || normalized === "lang";
}

async function extractErrorMessage(response: Response, language: string): Promise<string> {
  const fallback = `${pickLocalizedMessage(WORKFLOW_ERROR_MESSAGES.submitFailed, language)} (${response.status})`;
  const contentType = response.headers.get("content-type") ?? "";

  try {
    if (contentType.includes("application/json")) {
      const payload = (await response.json()) as Record<string, unknown> | null;
      if (payload && typeof payload === "object") {
        const directMessage = payload.message;
        if (typeof directMessage === "string" && directMessage.trim().length > 0) {
          return directMessage.trim();
        }
        const details = payload.details;
        if (isRecord(details) && typeof details.message === "string" && details.message.trim().length > 0) {
          return details.message.trim();
        }
      }
    } else {
      const text = await response.text();
      if (typeof text === "string" && text.trim().length > 0) {
        return text.trim();
      }
    }
  } catch {
    // ignore parsing failures
  }

  return fallback;
}

function formatResetMessage(language: string, seconds: number): string {
  const template = pickLocalizedMessage(WORKFLOW_RESET_MESSAGES, language);
  return template.replace("{seconds}", seconds.toString());
}

function extractAiSummary(extras: Record<string, unknown> | null): string | null {
  if (!extras) {
    return null;
  }

  for (const key of ["aiSummary", "summary", "result"]) {
    if (key in extras) {
      const value = normalizeSummaryValue(extras[key]);
      if (value) {
        return value;
      }
    }
  }

  const agentResponse = extras["agentResponse"];
  if (isRecord(agentResponse)) {
    for (const key of ["summary", "output", "text", "content", "message"]) {
      if (key in agentResponse) {
        const value = normalizeSummaryValue(agentResponse[key]);
        if (value) {
          return value;
        }
      }
    }
  }

  return null;
}

function normalizeSummaryValue(value: unknown): string | null {
  if (value == null) {
    return null;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (Array.isArray(value)) {
    const combined = value
      .map((entry) => normalizeSummaryValue(entry) ?? "")
      .filter((entry) => entry.length > 0)
      .join("\n\n");
    const trimmed = combined.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  if (isRecord(value)) {
    for (const key of ["summary", "output", "text", "content", "message", "value"]) {
      if (key in value) {
        const nested = normalizeSummaryValue(value[key]);
        if (nested) {
          return nested;
        }
      }
    }
  }

  return null;
}

function extractToolsUsed(extras: Record<string, unknown> | null): string[] {
  if (!extras) {
    return [];
  }

  const candidates: unknown[] = [];
  if ("aiToolsUsed" in extras) {
    candidates.push(extras["aiToolsUsed"]);
  }
  if ("toolsUsed" in extras) {
    candidates.push(extras["toolsUsed"]);
  }

  const agentResponse = extras["agentResponse"];
  if (isRecord(agentResponse) && "toolsUsed" in agentResponse) {
    candidates.push(agentResponse["toolsUsed"]);
  }

  const items = new Set<string>();
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      for (const entry of candidate) {
        if (typeof entry === "string") {
          const trimmed = entry.trim();
          if (trimmed.length > 0) {
            items.add(trimmed);
          }
        }
      }
    } else if (typeof candidate === "string") {
      const trimmed = candidate.trim();
      if (trimmed.length > 0) {
        items.add(trimmed);
      }
    }
  }

  return Array.from(items);
}

function pickLocalizedMessage(map: LocalizedMessageMap, language: string): string {
  if (map[language]) {
    return map[language];
  }

  const short = language.split("-")[0];
  if (map[short]) {
    return map[short];
  }

  if (map[FALLBACK_LANGUAGE]) {
    return map[FALLBACK_LANGUAGE];
  }

  const fallbackShort = FALLBACK_LANGUAGE.split("-")[0];
  if (map[fallbackShort]) {
    return map[fallbackShort];
  }

  const first = Object.values(map)[0];
  return typeof first === "string" ? first : fallbackShort;
}
